import Button from "atoms/buttons/button";
import ContactUsMessage from "organisms/resources/contact-us-message";
import GroupMemberRecord from "models/view-models/group-member-record";
import React, { useMemo, useState } from "react";
import { ButtonStyles } from "atoms/constants/button-styles";
import { ConfirmationModal } from "molecules/modals/confirmation-modal";
import { siteMap } from "internal-sitemap";
import { t } from "utilities/localization-utils";
import { useApplyTeamOnboarding } from "utilities/hooks/domain/team-onboarding/use-apply-team-onboarding";
import { useHistory } from "react-router-dom";
import Loader from "atoms/loaders/loader";
import ManageTeamMembers from "./manage-team-members";
import useCurrentIdentity from "utilities/hooks/use-current-identity";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export interface ManageTeamSettingsProps {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------
export const TEAM_OWNER_COUNT = 1; // Change if we ever add feature where team owner is a separate role and not part of the team
const CLASS_NAME = "c-manage-team-settings";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ManageTeamSettings: React.FC<ManageTeamSettingsProps> = () => {
    // State for team members changes (invites and members stored separately)
    const [teamMembersToMigrate, setTeamMembersToMigrate] = useState<
        GroupMemberRecord[]
    >([]);
    const [emailsToInvite, setEmailsToInvite] = useState<string[]>([]);

    // Confirmation modal display
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    // Custom hooks
    const history = useHistory();
    const { currentIdentity } = useCurrentIdentity();
    const { applyTeamOnboarding, saving } = useApplyTeamOnboarding({
        teamMembersToMove: teamMembersToMigrate,
        teamMembersToInvite: emailsToInvite,
        onApplySuccess: () => handleApplySuccess(),
        onApplyError: (e) => handleApplyError(e),
    });

    // Helper State
    const totalLicenses = currentIdentity?.getCurrentTeam()?.totalLicenses ?? 0;

    const totalInvitesToRemove = useMemo(
        () =>
            TEAM_OWNER_COUNT +
            emailsToInvite.length +
            teamMembersToMigrate.length -
            totalLicenses,
        [emailsToInvite.length, teamMembersToMigrate.length, totalLicenses]
    );

    // Handler methods
    const handleUpdateEmailsToInvite = (updatedEmailList) => {
        setEmailsToInvite([...updatedEmailList]);
    };

    const handleUpdateTeamMembersToMigrate = (
        updatedTeamMemberList: GroupMemberRecord[]
    ) => {
        setTeamMembersToMigrate([...updatedTeamMemberList]);
    };

    const handleSkipClick = () => {
        applyTeamOnboarding();
    };

    const handleContinueClick = () => {
        setShowConfirmationModal(true);
    };

    const handleConfirmChanges = async () => {
        await applyTeamOnboarding();
    };

    const handleApplySuccess = () => {
        history.push(siteMap.teamOnboarding.customize);
    };

    const handleApplyError = (error?: string) => {
        // Do something different based on error?
        setShowConfirmationModal(false);
    };

    // -----------------------------------------------------------------------------------------
    // #region Render
    // -----------------------------------------------------------------------------------------

    return (
        <div className={CLASS_NAME}>
            {saving && !showConfirmationModal ? (
                <Loader />
            ) : (
                <>
                    <ManageTeamMembers
                        totalLicenses={totalLicenses}
                        teamMembersToMigrate={teamMembersToMigrate}
                        emailsToInvite={emailsToInvite}
                        onUpdateTeamMembersToMigrate={
                            handleUpdateTeamMembersToMigrate
                        }
                        onUpdateEmailsToInvite={handleUpdateEmailsToInvite}
                    />
                    <div className={`${CLASS_NAME}__pending__actions`}>
                        <Button
                            disabled={false}
                            style={ButtonStyles.Tertiary}
                            onClick={() =>
                                history.push(siteMap.teamOnboarding.verify)
                            }>
                            {t("back")}
                        </Button>
                        <span className={`${CLASS_NAME}__pending__forward`}>
                            <Button
                                disabled={totalInvitesToRemove > 0}
                                style={ButtonStyles.Secondary}
                                onClick={handleSkipClick}>
                                {t("skip")}
                            </Button>
                            <Button
                                disabled={totalInvitesToRemove > 0}
                                onClick={handleContinueClick}>
                                {t("continue")}
                            </Button>
                        </span>
                    </div>
                    <div className={`${CLASS_NAME}__contact-us-message`}>
                        <ContactUsMessage />
                    </div>
                    <ConfirmationModal
                        isLoading={saving}
                        isVisible={showConfirmationModal}
                        message={t(
                            "teamOnboarding-confirmTeamMemberChangesMessage"
                        )}
                        label={t("confirmChanges")}
                        confirmButtonStyle={ButtonStyles.Primary}
                        onCancel={() => setShowConfirmationModal(false)}
                        onConfirm={handleConfirmChanges}
                    />
                </>
            )}
        </div>
    );
    // #endregion Render
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default ManageTeamSettings;

// #endregion Exports
